import ExcelJS from "exceljs";

const TEAL_HDR    = "28B3B4";
const TEAL_HDR2   = "1A8E8F";
const TEAL_TEXT   = "085041";
const BORDER_CLR  = "C8E8E6";
const ROW_EVEN_BG = "F0FAFA";
const ROW_ODD_BG  = "FFFFFF";

const COLUMN_DEFS = [
  { header: "Associate Name",      key: "fullName",          width: 26 },
  { header: "Level",               key: "levelCode",         width: 10 },
  { header: "Role Title",          key: "levelName",         width: 24 },
  { header: "Skills",              key: "skills",            width: 34 },
  { header: "Free Capacity (FTE)", key: "freeFte",           width: 20 },
  { header: "Used FTE",            key: "usedFte",           width: 12 },
  { header: "Days in Pool",        key: "daysInPool",        width: 14 },
  { header: "Active Allocations",  key: "activeAllocations", width: 34 },
  { header: "Pending Requests",    key: "pendingRequests",   width: 30 },
  { header: "In PIP",              key: "isInPip",           width: 10 },
  { header: "Status / Notice",     key: "statusNotice",      width: 22 },
];

function hairBorder(color = BORDER_CLR) {
  const s = { style: "hair", color: { argb: "FF" + color } };
  return { top: s, bottom: s, left: s, right: s };
}

function mediumBorder(color = TEAL_HDR2) {
  const m = { style: "medium", color: { argb: "FF" + color } };
  const t = { style: "thin",   color: { argb: "FF" + color } };
  return { top: m, bottom: m, left: t, right: t };
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

export async function exportScgPool(rows = [], activeFilter = "all") {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Psi360";
  wb.created = new Date();

  const ws = wb.addWorksheet("SCG Pool", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  ws.columns = COLUMN_DEFS.map((c) => ({
    header: c.header,
    key:    c.key,
    width:  c.width,
  }));

  // ── Header row ─────────────────────────────────────────────────────────────
  const headerRow = ws.getRow(1);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font      = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Arial" };
    cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + TEAL_HDR } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border    = mediumBorder();
  });

  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to:   { row: 1, column: COLUMN_DEFS.length },
  };

  // ── Data rows ──────────────────────────────────────────────────────────────
  rows.forEach((rowData, idx) => {
    const excelRowIdx = idx + 2;
    const isEven      = excelRowIdx % 2 === 0;
    const defaultBg   = isEven ? ROW_EVEN_BG : ROW_ODD_BG;

    const allocationsStr = Array.isArray(rowData.activeAllocations) && rowData.activeAllocations.length > 0
      ? rowData.activeAllocations.map(a => `${a.positionCode} (${a.macName || "—"}, ${a.fte} FTE)`).join("; ")
      : "None";

    const pendingStr = Array.isArray(rowData.pendingRequests) && rowData.pendingRequests.length > 0
      ? rowData.pendingRequests.map(p => `${p.requestCode} (${p.status}, ${p.fte} FTE)`).join("; ")
      : "None";

    let statusNotice = "Normal";
    if (rowData.exitDate) {
      statusNotice = `Resigned (LWD: ${fmtDate(rowData.exitDate)})`;
    } else if (rowData.isNew) {
      statusNotice = "New Joiner";
    }

    const row = ws.addRow({
      fullName: rowData.fullName ?? "",
      levelCode: rowData.levelCode ?? "",
      levelName: rowData.levelName ?? "",
      skills: Array.isArray(rowData.skills) ? rowData.skills.join(", ") : (rowData.skills ?? ""),
      freeFte: rowData.freeFte != null ? Number(rowData.freeFte) : 0,
      usedFte: rowData.usedFte != null ? Number(rowData.usedFte) : 0,
      daysInPool: rowData.daysInPool != null ? Number(rowData.daysInPool) : 0,
      activeAllocations: allocationsStr,
      pendingRequests: pendingStr,
      isInPip: rowData.isInPip ? "Yes" : "No",
      statusNotice,
    });

    row.height = 18;

    COLUMN_DEFS.forEach((col, colIdx) => {
      const cell = row.getCell(colIdx + 1);
      const isNum = col.key === "freeFte" || col.key === "usedFte" || col.key === "daysInPool";
      const isCenter = isNum || col.key === "levelCode" || col.key === "isInPip";

      cell.font      = { name: "Arial", size: 10, color: { argb: "FF" + TEAL_TEXT } };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + defaultBg } };
      cell.alignment = { horizontal: isCenter ? "center" : "left", vertical: "middle" };
      cell.border    = hairBorder();

      if (col.key === "freeFte" || col.key === "usedFte") {
        cell.numFmt = "0.00";
      }
    });
  });

  // ── Summary row ────────────────────────────────────────────────────────────
  const summaryRow = ws.addRow({
    fullName: `Total in Pool: ${rows.length}`,
  });
  summaryRow.height = 20;
  summaryRow.getCell(1).font = { name: "Arial", size: 10, bold: true, color: { argb: "FF" + TEAL_TEXT } };
  summaryRow.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + ROW_EVEN_BG } };

  // ── Download ───────────────────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href    = url;
  const today = new Date().toISOString().split("T")[0];
  const filterSuffix = activeFilter && activeFilter !== "all" ? `_${activeFilter}` : "";
  a.download  = `scg_pool${filterSuffix}_${today}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
